/** Per-teammate workload heat bar — one segmented bar per person split into
 *  overdue (red) · due-soon ≤7d (amber) · open (blue) · milestones (violet).
 *  Total open items becomes the bar's magnitude, normalised across the roster. */
export function WorkloadHeatBar({ row, maxTotal }) {
  const total = row.total_open_items || 0;
  const magnitude = maxTotal > 0 ? (total / maxTotal) * 100 : 0;
  const seg = (n) => (total > 0 ? (n / total) * 100 : 0);
  const overdue = seg(row.overdue_commitments + row.overdue_milestones);
  const dueSoon = seg(row.due_soon_commitments);
  const openCommits = seg(Math.max(0, row.open_commitments - row.overdue_commitments - row.due_soon_commitments));
  const openMs = seg(Math.max(0, row.open_milestones - row.overdue_milestones));

  return (
    <div className="w-full" data-testid={`tw-heat-${row.user_id}`}>
      {/* Outer track sized by relative magnitude across the roster */}
      <div className="relative h-2 rounded-full bg-[hsl(var(--surface-2))] overflow-hidden">
        <div className="absolute inset-y-0 left-0 flex" style={{ width: `${Math.max(6, magnitude)}%` }}>
          {overdue > 0 && (
            <div className="h-full" style={{ width: `${overdue}%`, background: 'hsl(var(--danger))' }}
                 title={`Overdue: ${row.overdue_commitments + row.overdue_milestones}`} />
          )}
          {dueSoon > 0 && (
            <div className="h-full" style={{ width: `${dueSoon}%`, background: 'hsl(var(--warning))' }}
                 title={`Due in ≤7d: ${row.due_soon_commitments}`} />
          )}
          {openCommits > 0 && (
            <div className="h-full" style={{ width: `${openCommits}%`, background: 'hsl(var(--primary))' }}
                 title={`Open commitments: ${row.open_commitments}`} />
          )}
          {openMs > 0 && (
            <div className="h-full" style={{ width: `${openMs}%`, background: 'hsl(var(--info))' }}
                 title={`Open milestones: ${row.open_milestones}`} />
          )}
        </div>
      </div>
      <div className="mt-1 flex items-center gap-2 text-[10.5px] text-[hsl(var(--foreground-muted))]">
        <span className="tabular-nums font-semibold text-[hsl(var(--foreground))]">{total}</span>
        {row.overdue_commitments + row.overdue_milestones > 0 && (
          <span className="text-[hsl(var(--danger))]">● {row.overdue_commitments + row.overdue_milestones} overdue</span>
        )}
        {row.due_soon_commitments > 0 && (
          <span className="text-[hsl(var(--warning))]">● {row.due_soon_commitments} due ≤7d</span>
        )}
      </div>
    </div>
  );
}

/** Small colour-key legend for the heat bar. Renders once above the table. */
export function WorkloadHeatLegend() {
  return (
    <div className="flex items-center gap-3 text-[11px] text-[hsl(var(--foreground-muted))] flex-wrap"
         data-testid="tw-heat-legend">
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: 'hsl(var(--danger))' }} />
        Overdue
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: 'hsl(var(--warning))' }} />
        Due ≤7d
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: 'hsl(var(--primary))' }} />
        Open commitments
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: 'hsl(var(--info))' }} />
        Open milestones
      </span>
    </div>
  );
}
